import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import defaultFormState, { requiredFields } from '@models/apiKey'

import PageContext from '@contexts/pageContext'

import * as apiKeyActions from '@redux/modules/apiKey'
import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import type { ApiKeyModel, ApiKeyRequestOptions } from '@models/types'

type CreateApiKeyParams = {
  apiKeyParams: Partial<ApiKeyModel>,
  dispatch: AppDispatch,
  requestOptions?: ApiKeyRequestOptions,
}

const createApiKey = (params: CreateApiKeyParams) => {
  const { apiKeyParams, dispatch, requestOptions } = params
  const { createApiKey: createFn } = apiKeyActions

  return dispatch(createFn(apiKeyParams, requestOptions))
}

type UpdateApiKeyParams = {
  apiKey: ApiKeyModel,
  apiKeyParams: Partial<ApiKeyModel>,
  dispatch: AppDispatch,
  requestOptions?: ApiKeyRequestOptions,
}

const updateApiKey = (params: UpdateApiKeyParams) => {
  const {
    dispatch, apiKey, apiKeyParams, requestOptions,
  } = params
  const { updateApiKey: updateFn } = apiKeyActions

  const updatedParams = {
    id: apiKey.id,
    ...apiKeyParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteApiKeyParams = {
  apiKey: DeleteParams<ApiKeyModel>,
  dispatch: AppDispatch,
}

const deleteApiKey = (params: DeleteApiKeyParams) => {
  const { dispatch, apiKey } = params
  const { deleteApiKey: deleteFn } = apiKeyActions

  return dispatch(deleteFn(apiKey))
}

type BulkCreateParams = {
  agency_id: number,
  title: string,
}

type BulkCreateApiKeys = {
  bulkCreateParams: BulkCreateParams,
  dispatch: AppDispatch,
}

const bulkCreateApiKeys = (params: BulkCreateApiKeys) => {
  const { bulkCreateParams, dispatch } = params
  const { bulkCreateApiKeys: bulkCreateFn } = apiKeyActions

  return dispatch(bulkCreateFn(bulkCreateParams))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useApiKeyForm(
  apiKey: Partial<ApiKeyModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const apiKeyForm = useForm(
    defaultFormState,
    { entity: apiKey, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [apiKey.id, apiKey.cache_key],
  )

  return {
    ...apiKeyForm,
  }
}

export const useRelations = (apiKey: Partial<ApiKeyModel> = {}) => {
  const { default_user_id } = apiKey || {}

  const users = useSelector(reduxState => reduxState.entities.users)

  const user = default_user_id ? users[default_user_id] || {} : {}

  return { user }
}

function useApiKey(initEntity: Partial<ApiKeyModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: apiKey }: { entity: ApiKeyModel } = useLatestEntity(initEntity, 'apiKeys')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { user } = useRelations(apiKey)

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.apiKeys)

  return {
    callbacks: {
      bulkCreateApiKeys: (bulkCreateParams: BulkCreateParams) => bulkCreateApiKeys({ bulkCreateParams, dispatch }),
      createApiKey: (apiKeyParams: Partial<ApiKeyModel>, entityOptions?: ApiKeyRequestOptions) => (
        createApiKey({ apiKeyParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditApiKey: (customPayload: object) => launchModal({
        callbacks,
        modalKey: 'CreateOrEditApiKeyModal',
        payload: { apiKey, ...customPayload },
      }),
      deleteApiKey: () => deleteApiKey({ dispatch, apiKey }),
      manageBulkCreateApiKeys: () => launchModal({
        callbacks,
        modalKey: 'BulkCreateApiKeysModal',
        payload: { apiKey },
      }),
      updateApiKey: (apiKeyParams: Partial<ApiKeyModel>, entityOptions?: ApiKeyRequestOptions) => (
        updateApiKey({
          apiKey, apiKeyParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    apiKey,
    creating,
    deleting,
    loading,
    updating,
    user,
  }
}

export default useApiKey
